/**
 * Which server request ids this dashboard view will still accept results for.
 *
 * A `BiChartsSettled` event carries `turn_id` == the `request_id` the server
 * answered a `/query` with. The client must ignore an event from a superseded
 * request, or it paints numbers computed under a filter state the user has
 * already moved on from (design doc section 6).
 *
 * A SET rather than one current id, because more than one request is
 * legitimately outstanding at a time: the dashboard-wide batch, plus whatever
 * per-chart retries the user has clicked. Tracking a single id means whichever
 * request answered last silently orphans the other's events, and those charts
 * sit loading forever with no timeout path in the UI. A filter change clears
 * the set outright, since it supersedes in-flight retries just as much as the
 * batch that spawned them.
 *
 * Pure and separately tested, per this app's rule that `bi-query.ts` keeps
 * only the genuinely non-pure parts (network, Pusher, React state).
 */
export interface AcceptedRequests {
  /** Stop accepting every id currently tracked. Called when a new generation starts. */
  reset(): void;
  /** Begin accepting results for a request the server has just acknowledged. */
  accept(requestId: string): void;
  /** Whether an inbound event's `turn_id` belongs to a request still wanted. */
  isAccepted(requestId: string): boolean;
  /** Current size. Exposed for tests and debugging, not for control flow. */
  readonly size: number;
}

export function createAcceptedRequests(): AcceptedRequests {
  let ids = new Set<string>();
  return {
    reset() {
      ids = new Set<string>();
    },
    accept(requestId: string) {
      ids.add(requestId);
    },
    isAccepted(requestId: string) {
      return ids.has(requestId);
    },
    get size() {
      return ids.size;
    },
  };
}
